import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Check, CircleUser, LogOut } from 'lucide-react';
import { LayerDepthContext, useModalLayer } from '@/lib/layerManager';
import { navigateApp } from '@/lib/navigation';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useGlobalOrganization } from '@/hooks/useGlobalOrganization';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { formatRole } from '@/lib/userRole';

const PANEL_WIDTH = 288;

/** Profile icon for the top bar; opens a small card with the user, an org switcher and logout. */
export function ProfileMenu({ onLogout }: { onLogout: () => void }) {
  const { user } = useAuth();
  const { organizations, selectedOrgId, switchOrganization } = useGlobalOrganization();
  const { addNotification } = useNotifications();
  // Mobile skips the card: the icon goes straight to the profile page (same switcher + logout).
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  // Same as NotificationBell: no back entry, it would race the "See all" navigation.
  const layer = useModalLayer({ enabled: open, onClose: close, backNavigation: false });

  const place = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(PANEL_WIDTH, window.innerWidth - 16);
    setPosition({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right), width });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('resize', place);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, place, close]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (isMobile ? navigateApp('/profile') : setOpen(o => !o))}
        className="p-2 -m-2 text-gray-700 hover:text-gray-900 hover:bg-gray-300 rounded-lg transition-colors duration-200 shrink-0"
        aria-label="Profile"
      >
        <CircleUser className="h-5 w-5" />
      </button>

      {open && createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
          <div className="fixed inset-0" style={layer.style} onClick={close}>
            <div
              className="fixed flex flex-col overflow-hidden rounded-xl border border-secondary-200 bg-white shadow-xl"
              style={position}
              onClick={e => e.stopPropagation()}
              role="dialog"
              aria-label="Profile"
            >
              <div className="px-4 py-3 border-b border-secondary-200">
                {/* ponytail: Firebase displayName is often unset; there's no user-profile endpoint to fall back on */}
                {user?.displayName && <p className="truncate text-sm font-semibold text-secondary-900">{user.displayName}</p>}
                <p className="truncate text-sm text-secondary-600">{user?.email}</p>
              </div>

              {organizations.length > 0 && (
                <div className="py-1 border-b border-secondary-200">
                  {organizations.map(org => {
                    const isActive = org.organization_id === selectedOrgId;
                    return (
                      <button
                        key={org.organization_id}
                        type="button"
                        disabled={isActive}
                        onClick={() => {
                          close();
                          switchOrganization(org.organization_id);
                          addNotification(notificationService.success(`Switched to ${org.organization_name}`));
                        }}
                        className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm ${isActive ? 'font-medium text-secondary-900' : 'text-secondary-700 hover:bg-secondary-100'}`}
                      >
                        <Building2 className="h-4 w-4 shrink-0 text-secondary-400" />
                        <span className="flex-1 truncate">{org.organization_name} <span className="text-secondary-400">({formatRole(org.role)})</span></span>
                        {isActive && <Check className="h-4 w-4 shrink-0 text-primary-600" />}
                      </button>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                onClick={() => { close(); onLogout(); }}
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-red-600 hover:bg-secondary-100"
              >
                <LogOut className="h-4 w-4" /> Log out
              </button>

              <button
                type="button"
                onClick={() => { close(); navigateApp('/profile'); }}
                className="border-t border-secondary-200 bg-secondary-100 px-4 py-3 text-center text-sm font-medium text-primary-600 hover:bg-secondary-200"
              >
                See all
              </button>
            </div>
          </div>
        </LayerDepthContext.Provider>,
        document.body,
      )}
    </>
  );
}
